import {
    computed,
    type ComputedRef,
    type MaybeRefOrGetter,
    nextTick,
    type Ref,
    ref,
    toValue,
    watch,
} from 'vue'
import { CombinedError } from '@urql/vue'
import { GraphQLError } from 'graphql/index'
import type {
    AuthorizationError,
    CoercionError,
    RateLimitError,
    ValidationError,
} from '@/graphql/api/types'
import { isNotUndefinedOrNull } from '@/helpers'
import type {
    BackendError,
    ErrorCollection,
    ErrorKeyOrKeys,
} from '@/helpers/errors/collection'
import {
    createErrorCollection,
    mergeCollections,
} from '@/helpers/errors/collection'

export interface ErrorCollectionWithTraces extends ErrorCollection {
    readonly amazonTraceId: string | undefined
    readonly serverErrors: GraphQLServerError[]
}

type GraphQLServerError = {
    message: string
    sentryEventId: string | undefined
}

export type ResponseAndPath<DATA> = {
    path: ExtractDataRootPaths<DATA>
    response: MutationOrQueryState<DATA>
}

export type ExtractDataRootPaths<STATE> = Exclude<
    keyof Exclude<MutationOrQueryState<STATE>['data']['value'], undefined>,
    '__typename' | number | symbol
>

export type MutationOrQueryStateUnref<DATA> = {
    data: DATA | undefined
    error: CombinedError | undefined
    extensions: Record<string, any> | undefined
}

export type MutationOrQueryState<DATA> = {
    [Key in keyof MutationOrQueryStateUnref<DATA>]: Ref<
        MutationOrQueryStateUnref<DATA>[Key]
    >
}

export function useLastResponseErrorAtPath(
    errors: ResponseAndPath<any>[],
    error?: Ref<ErrorCollectionWithTraces>
): ComputedRef<ErrorCollectionWithTraces> {
    const errorRef = error ?? ref(createEmptyCollection())

    errors.forEach((e: ResponseAndPath<any>) => {
        watch(
            () => e,
            (err) => {
                if (!err.response.error.value) {
                    return
                }

                errorRef.value = responseToErrorCollectionWithTraces(
                    err.response
                ).children(err.path)
            },
            { immediate: true, deep: true }
        )
    })

    return computed(() => errorRef.value)
}

export function useLastResponseError(
    errors: MutationOrQueryState<any>[],
    error?: Ref<ErrorCollectionWithTraces>
): ComputedRef<ErrorCollectionWithTraces> {
    const errorRef = error ?? ref(createEmptyCollection())

    errors.forEach((e: MutationOrQueryState<any>) => {
        watch(
            () => e,
            (response) => {
                if (!response.error.value) {
                    return
                }

                errorRef.value = responseToErrorCollectionWithTraces(response)
            },
            { immediate: true, deep: true }
        )
    })

    return computed(() => errorRef.value)
}

export function createResponseAndPath<DATA>(
    response: MutationOrQueryState<DATA>,
    path: ExtractDataRootPaths<DATA>
): ResponseAndPath<DATA> {
    return {
        path,
        response,
    }
    // return computed(() => {
    //     return createResponseErrorWithPath(response, path)
    // })
}

// function createResponseErrorWithPath<DATA>(
//     response: MutationOrQueryState<DATA>,
//     path: ExtractDataRootPaths<DATA>
// ): ResponseAndPath<DATA> {
//     return {
//         path,
//         response,
//     }
// }

export function useResponseError<T>(
    response: MutationOrQueryState<T>
): ComputedRef<ErrorCollectionWithTraces> {
    return computed(() => {
        return responseToErrorCollectionWithTraces(response)
    })
}

export function useResponseErrorAtPath<DATA>(
    response: MutationOrQueryState<DATA>,
    path: ExtractDataRootPaths<DATA>
): ComputedRef<ErrorCollectionWithTraces> {
    return computed(() => {
        return responseToErrorCollectionWithTraces(response).children(path)
    })
}

export function useAmazonTraceId<T>(
    response: MutationOrQueryState<T>
): ComputedRef<string | undefined> {
    return computed(() => extractAmazonTraceId(toValue(response.extensions)))
}

function responseToErrorCollectionWithTraces<T>(
    response: MutationOrQueryState<T>
): ErrorCollectionWithTraces {
    const collection = createCollectionForResponse(response)

    const amazonTraceId = extractAmazonTraceId(toValue(response.extensions))

    const serverErrors = extractGraphQLServerErrors(toValue(response.error))

    return createErrorCollectionWithTraces(
        collection,
        amazonTraceId,
        serverErrors
    )
}

function extractAmazonTraceId(
    extensions: Record<string, any> | undefined
): string | undefined {
    return extensions?.['amazonTraceId']
}

function extractGraphQLServerErrors(
    error: CombinedError | undefined
): GraphQLServerError[] {
    if (!error) {
        return []
    }

    return error.graphQLErrors
        .filter((error) => {
            return [
                isValidationError,
                isCoercionError,
                isAuthorizationError,
            ].every((validator) => !validator(error))
            // return !(isValidationError(error) || isCoercionError(error))
        })
        .map((error): GraphQLServerError => {
            const sentryEventId = error.extensions?.['sentryEventId']
            return {
                message: error.message,
                sentryEventId:
                    typeof sentryEventId === 'string'
                        ? sentryEventId
                        : undefined,
            }
        })
}

export function createEmptyCollection(): ErrorCollectionWithTraces {
    return createErrorCollectionWithTraces(
        createErrorCollection([]),
        undefined,
        []
    )
}

export function withEmptyTraces(
    collection: ErrorCollection
): ErrorCollectionWithTraces {
    return createErrorCollectionWithTraces(collection, undefined, [])
}

export function createErrorCollectionWithTraces(
    collection: ErrorCollection,
    amazonTraceId: string | undefined,
    serverErrors: GraphQLServerError[]
): ErrorCollectionWithTraces {
    const ret: ErrorCollectionWithTraces = {
        get isInternalServerError() {
            return collection.isInternalServerError
        },
        get isUnauthorized() {
            return collection.isUnauthorized
        },
        get unmapped() {
            return collection.unmapped
        },
        get length() {
            return collection.length
        },
        has(path: ErrorKeyOrKeys) {
            return collection.has(path)
        },
        children(path: ErrorKeyOrKeys): typeof this {
            return createErrorCollectionWithTraces(
                collection.children(path),
                amazonTraceId,
                serverErrors
            )
        },
        get(path: ErrorKeyOrKeys): BackendError[] | undefined {
            return collection.get(path)
        },
        last(path: ErrorKeyOrKeys): string | undefined {
            return collection.last(path)
        },
        merge(withCollection: typeof this): typeof this {
            return createErrorCollectionWithTraces(
                collection.merge(withCollection),
                amazonTraceId,
                serverErrors
            )
        },
        get amazonTraceId(): string | undefined {
            return amazonTraceId
        },
        get serverErrors(): GraphQLServerError[] {
            return serverErrors
        },
    }

    if (import.meta.env.DEV) {
        // @ts-expect-error alabala
        ret.debug = () => collection.debug()
    }

    return ret
}

function createCollectionForResponse<T>(
    response: MutationOrQueryState<T>
): ErrorCollection {
    const err = response.error.value

    if (!err) {
        return createErrorCollection([])
    }

    return combinedErrorToCollection(err)
}

function combinedErrorToCollection(err: CombinedError): ErrorCollection {
    return createErrorCollection(combinedErrorToBackendErrors(err))
}

function combinedErrorToBackendErrors(err: CombinedError): BackendError[] {
    return err.graphQLErrors.reduce(
        (acc: BackendError[], curr): BackendError[] => {
            const path = [...(curr.path ? curr.path : [])]

            if (isValidationError(curr)) {
                return [
                    ...acc,
                    ...curr.extensions.violations.map((e): BackendError => {
                        return {
                            message: e.message,
                            path: [...path, ...e.path],
                            isInternalServerError: false,
                            isUnauthorized: false,
                        }
                    }),
                ]
            }

            if (isCoercionError(curr)) {
                return [
                    ...acc,
                    {
                        message: curr.extensions.violation.message,
                        path: [...path, ...curr.extensions.violation.path],
                        isInternalServerError: false,
                        isUnauthorized: false,
                    },
                ]
            }

            if (isAuthorizationError(curr)) {
                return [
                    ...acc,
                    {
                        message: curr.message,
                        path: [...path],
                        isInternalServerError: false,
                        isUnauthorized: true,
                    },
                ]
            }

            return [
                ...acc,
                {
                    message: curr.message,
                    path: path,
                    isInternalServerError: true,
                    isUnauthorized: false,
                },
            ]
        },
        []
    )
}

export function useLastError(
    errors: Ref<ErrorCollectionWithTraces>[],
    error?: Ref<ErrorCollectionWithTraces>
): ComputedRef<ErrorCollectionWithTraces> {
    const errorRef = error ?? ref(createEmptyCollection())

    errors.forEach((e) => {
        watch(e, (err) => {
            errorRef.value = err
        })
    })

    // TODO Test that the initial error is always the last one with errors in it rather than last of the list
    errors.forEach((e) => {
        if (e.value.length) {
            errorRef.value = e.value
        }
    })

    return computed(() => errorRef.value)
}

export function useSimpleErrorCollection(
    errors: MaybeRefOrGetter<
        MaybeRefOrGetter<BackendError | undefined | null>[]
    >
): ComputedRef<ErrorCollection> {
    return computed(() =>
        createErrorCollection(
            toValue(errors).map(toValue).filter(isNotUndefinedOrNull)
        )
    )
}

// TODO This needs to be reworked - main error + other errors
export function useMergedCollection(
    collection: Readonly<Ref<ErrorCollectionWithTraces>>,
    collections: MaybeRefOrGetter<MaybeRefOrGetter<ErrorCollection>[]>
): ComputedRef<ErrorCollectionWithTraces> {
    return computed(() => {
        return createErrorCollectionWithTraces(
            mergeCollections([
                collection.value,
                ...toValue(collections).map(toValue),
            ]),
            collection.value.amazonTraceId,
            collection.value.serverErrors
        )
    })
}

// // TODO Deprecate in favor of useResponseError - once it's replaced in tests, remove?
// function useErrorCollection(
//     error: MaybeRef<CombinedError | undefined>
// ): ComputedRef<ErrorCollection> {
//     return computed(() => {
//         const err = isRef(error) ? error.value : error
//
//         if (!err) {
//             return createErrorCollection([])
//         }
//
//         return combinedErrorToCollection(err)
//     })
// }

function isValidationError(
    e: GraphQLError
): e is GraphQLError & ValidationError {
    return isCustomError(e, 'validation')
}

export function isRateLimitError(
    e: GraphQLError
): e is GraphQLError & ValidationError {
    return isCustomError(e, 'rateLimit')
}

function isCoercionError(e: GraphQLError): e is GraphQLError & CoercionError {
    return isCustomError(e, 'coercion_error')
}

function isAuthorizationError(
    e: GraphQLError
): e is GraphQLError & AuthorizationError {
    return isCustomError(e, 'authorization')
}

function isCustomError<
    T extends
        | ValidationError
        | CoercionError
        | AuthorizationError
        | RateLimitError,
>(
    e: GraphQLError,
    category: T['extensions']['category']
): e is GraphQLError & T {
    if (!('extensions' in e)) {
        return false
    }

    const extensions = e.extensions

    if (!('category' in extensions)) {
        return false
    }

    return extensions['category'] === category
}

if (import.meta.vitest) {
    const { expect, describe, test } = import.meta.vitest

    describe('useResponseError', () => {
        const firstGraphQLError = new GraphQLError('error wan', {
            path: ['MySomewhereQuery1'],
            extensions: {
                sentryEventId: 'ala bala portokala',
                category: 'validation',
                violations: [
                    {
                        path: ['user', 'username'],
                        message: 'must be at least 2 chars 1',
                        code: 'sumthin? idk',
                    },
                    {
                        path: ['user', 'email'],
                        message: 'must be a valid email',
                        code: 'dun kere, oke?',
                    },
                ],
            },
        })

        const secondGraphQLError = new GraphQLError('dve', {
            path: ['MySomewhereQuery1'],
            extensions: {
                sentryEventId: 'tra la la',
                violations: [
                    {
                        path: ['user', 'username'],
                        message: 'must be at least 2 chars 2',
                        code: 'sumthin? idk',
                    },
                    {
                        path: ['user', 'email'],
                        message: 'must be a valid email',
                        code: 'dun kere, oke?',
                    },
                ],
            },
        })

        const thirdGraphQLError = new GraphQLError('tre', {
            path: ['MySomewhereQuery1'],
            extensions: {
                sentryEventId: 'blah',
                violations: [
                    {
                        path: ['user', 'username'],
                        message: 'must be at least 2 chars 3',
                        code: 'sumthin? idk',
                    },
                    {
                        path: ['user', 'email'],
                        message: 'must be a valid email',
                        code: 'dun kere, oke?',
                    },
                ],
            },
        })

        const fourthGraphQLError = new GraphQLError('error wan', {
            path: ['MySomewhereQuery1'],
            extensions: {
                sentryEventId: 'ala bala portokala',
                category: 'validation',
                violations: [
                    {
                        path: ['user', 'username'],
                        message: 'must be at least 2 chars 4',
                        code: 'sumthin? idk',
                    },
                    {
                        path: ['user', 'email'],
                        message: 'must be a valid email',
                        code: 'dun kere, oke?',
                    },
                ],
            },
        })

        test('useResponseError', async () => {
            const firstCombinedError = new CombinedError({
                graphQLErrors: [
                    firstGraphQLError,
                    secondGraphQLError,
                    fourthGraphQLError,
                ],
            })

            const data = {
                test: 'asd',
            }
            const response: MutationOrQueryState<typeof data> = {
                data: ref(data),
                error: ref(firstCombinedError),
                extensions: ref({
                    amazonTraceId: 'amazon trace id',
                }),
            }

            // eslint-disable-next-line vue-composable/composable-placement
            const collection = useResponseError(response)

            expect(collection.value.amazonTraceId).toBe('amazon trace id')
            expect(collection.value.serverErrors).toStrictEqual([
                {
                    message: 'dve',
                    sentryEventId: 'tra la la',
                },
            ])
            expect(
                collection.value.last('MySomewhereQuery1.user.username')
            ).toBe('must be at least 2 chars 4')
            expect(
                collection.value
                    .children('MySomewhereQuery1')
                    .last('user.username')
            ).toBe('must be at least 2 chars 4')
            expect(
                collection.value
                    .children('MySomewhereQuery1.user')
                    .last('username')
            ).toBe('must be at least 2 chars 4')

            response.error.value = new CombinedError({
                graphQLErrors: [
                    firstGraphQLError,
                    secondGraphQLError,
                    thirdGraphQLError,
                ],
            })
            response.extensions.value = {
                amazonTraceId: 'another trace ID',
            }

            await nextTick()

            expect(collection.value.amazonTraceId).toBe('another trace ID')
            expect(collection.value.serverErrors).toStrictEqual([
                {
                    message: 'dve',
                    sentryEventId: 'tra la la',
                },
                {
                    message: 'tre',
                    sentryEventId: 'blah',
                },
            ])
            expect(
                collection.value.last('MySomewhereQuery1.user.username')
            ).toBe('must be at least 2 chars 1')
            expect(
                collection.value
                    .children('MySomewhereQuery1')
                    .last('user.username')
            ).toBe('must be at least 2 chars 1')
            expect(
                collection.value
                    .children('MySomewhereQuery1.user')
                    .last('username')
            ).toBe('must be at least 2 chars 1')
        })

        test('useResponseErrorAtPath', async () => {
            const firstCombinedError = new CombinedError({
                graphQLErrors: [
                    firstGraphQLError,
                    secondGraphQLError,
                    fourthGraphQLError,
                ],
            })

            const data = {
                MySomewhereQuery1: 'asd',
            }
            const response: MutationOrQueryState<typeof data> = {
                data: ref(data),
                error: ref(firstCombinedError),
                extensions: ref({
                    amazonTraceId: 'amazon trace id',
                }),
            }

            // eslint-disable-next-line vue-composable/composable-placement
            const collection = useResponseErrorAtPath(
                response,
                'MySomewhereQuery1'
            )

            expect(collection.value.amazonTraceId).toBe('amazon trace id')
            expect(collection.value.serverErrors).toStrictEqual([
                {
                    message: 'dve',
                    sentryEventId: 'tra la la',
                },
            ])
            expect(collection.value.last('user.username')).toBe(
                'must be at least 2 chars 4'
            )
            expect(collection.value.children('user').last('username')).toBe(
                'must be at least 2 chars 4'
            )

            response.error.value = new CombinedError({
                graphQLErrors: [
                    firstGraphQLError,
                    secondGraphQLError,
                    thirdGraphQLError,
                ],
            })
            response.extensions.value = {
                amazonTraceId: 'another trace ID',
            }

            await nextTick()

            expect(collection.value.amazonTraceId).toBe('another trace ID')
            expect(collection.value.serverErrors).toStrictEqual([
                {
                    message: 'dve',
                    sentryEventId: 'tra la la',
                },
                {
                    message: 'tre',
                    sentryEventId: 'blah',
                },
            ])
            expect(collection.value.last('user.username')).toBe(
                'must be at least 2 chars 1'
            )

            expect(collection.value.children('user').last('username')).toBe(
                'must be at least 2 chars 1'
            )
        })
    })

    // TODO Reinstate those tests - API changed
    // describe('useMergedCollection', () => {
    //     const firstCollectionErrors: BackendError[] = [
    //         {
    //             message: 'error one',
    //             path: ['user', 'username'],
    //             isInternalServerError: false,
    //             isUnauthorized: false,
    //         },
    //         {
    //             message: 'error two',
    //             path: ['accessKey'],
    //             isInternalServerError: false,
    //             isUnauthorized: false,
    //         },
    //         {
    //             message: 'error three',
    //             path: ['accessKey'],
    //             isInternalServerError: false,
    //             isUnauthorized: false,
    //         },
    //     ]
    //
    //     const secondCollectionErrors: BackendError[] = [
    //         {
    //             message: 'first roles field error',
    //             path: ['user', 'roles', 0],
    //             isInternalServerError: false,
    //             isUnauthorized: false,
    //         },
    //         {
    //             message: 'second roles field error',
    //             path: ['user', 'roles', 1],
    //             isInternalServerError: false,
    //             isUnauthorized: false,
    //         },
    //     ]
    //
    //     const thirdCollectionErrors: BackendError[] = [
    //         {
    //             message: 'Password should contain at least one capital letter',
    //             path: ['user', 'password', 'first'],
    //             isInternalServerError: false,
    //             isUnauthorized: false,
    //         },
    //         {
    //             message:
    //                 'Password should contain at least one special character',
    //             path: ['user', 'password', 'first'],
    //             isInternalServerError: false,
    //             isUnauthorized: false,
    //         },
    //         {
    //             message: 'Password should match in both fields',
    //             path: ['user', 'password', 'second'],
    //             isInternalServerError: false,
    //             isUnauthorized: false,
    //         },
    //     ]
    //
    //     test('Test merge collections', () => {
    //         const firstCollection = createErrorCollection(firstCollectionErrors)
    //         const secondCollection: Ref<ErrorCollection> = ref(
    //             createErrorCollection(secondCollectionErrors)
    //         )
    //         const thirdCollection = computed(() =>
    //             createErrorCollection(thirdCollectionErrors)
    //         )
    //
    //         const mergedCollection = useMergedCollection([
    //             firstCollection,
    //             secondCollection,
    //             thirdCollection,
    //         ]).value
    //
    //         expect(mergedCollection.last('accessKey')).toBe('error three')
    //         expect(mergedCollection.has('accessKey')).toBe(true)
    //         expect(mergedCollection.last('non-existent')).toBeUndefined()
    //         expect(mergedCollection.has('non-existent')).toBe(false)
    //
    //         const accessKeyErrors = mergedCollection.get('accessKey')
    //         expect(accessKeyErrors).not.toBeUndefined()
    //         expect(Array.isArray(accessKeyErrors)).toEqual(true)
    //
    //         if (typeof accessKeyErrors !== 'undefined') {
    //             if (accessKeyErrors[0]) {
    //                 expect(accessKeyErrors[0].message).toBe('error two')
    //             }
    //
    //             if (accessKeyErrors[1]) {
    //                 expect(accessKeyErrors[1].message).toBe('error three')
    //             }
    //         }
    //
    //         expect(mergedCollection.last('user.username')).toBe('error one')
    //
    //         expect(mergedCollection.last('user.roles.0')).toBe(
    //             'first roles field error'
    //         )
    //         expect(mergedCollection.has('user.roles.0')).toBe(true)
    //         expect(mergedCollection.last('user.roles.1')).toBe(
    //             'second roles field error'
    //         )
    //         expect(mergedCollection.has('user.roles.1')).toBe(true)
    //
    //         // Test errors array
    //         expect(mergedCollection.has(['user', 'roles', 1])).toBe(true)
    //         expect(mergedCollection.last(['user', 'roles', 1])).toBe(
    //             'second roles field error'
    //         )
    //
    //         const userErrors = mergedCollection.children('user')
    //         if (typeof userErrors !== 'undefined') {
    //             expect(userErrors.last('roles.0')).toBe(
    //                 'first roles field error'
    //             )
    //             expect(userErrors.has('roles.0')).toBe(true)
    //             expect(userErrors.last('roles.1')).toBe(
    //                 'second roles field error'
    //             )
    //             expect(userErrors.has('roles.1')).toBe(true)
    //
    //             const rolesErrors = userErrors.children('roles')
    //             if (typeof rolesErrors !== 'undefined') {
    //                 expect(rolesErrors.last(0)).toBe('first roles field error')
    //                 expect(rolesErrors.has(0)).toBe(true)
    //                 expect(rolesErrors.last(1)).toBe('second roles field error')
    //                 expect(rolesErrors.has(1)).toBe(true)
    //             }
    //         }
    //
    //         const rolesErrors = mergedCollection.children('user.roles')
    //         if (typeof rolesErrors !== 'undefined') {
    //             expect(rolesErrors.last(0)).toBe('first roles field error')
    //             expect(rolesErrors.has(0)).toBe(true)
    //             expect(rolesErrors.last(1)).toBe('second roles field error')
    //             expect(rolesErrors.has(1)).toBe(true)
    //         }
    //
    //         expect(mergedCollection.last('user.password.first')).toBe(
    //             'Password should contain at least one special character'
    //         )
    //         expect(mergedCollection.has('user.password.first')).toBe(true)
    //         expect(mergedCollection.last('user.password.second')).toBe(
    //             'Password should match in both fields'
    //         )
    //         expect(mergedCollection.has('user.password.second')).toBe(true)
    //
    //         // expect(mergedCollection.children('non-existent')).toBe(emptyErrors)
    //         expect(mergedCollection.children('non-existent').last('faf')).toBe(
    //             undefined
    //         )
    //         expect(mergedCollection.get('non-existent')).toStrictEqual([])
    //     })
    // })

    // todo func deprecated in favor of useResponseError - move cases there
    // describe('useErrorCollection', () => {
    //     const graphQLErrors: GraphQLError[] = [
    //         new GraphQLError('error wan', {
    //             path: ['MySomewhereQuery'],
    //             extensions: {
    //                 category: 'validation',
    //                 violations: [
    //                     {
    //                         path: ['user', 'username'],
    //                         message: 'must be at least 2 chars',
    //                         code: 'sumthin? idk',
    //                     },
    //                     {
    //                         path: ['user', 'email'],
    //                         message: 'must be a valid email',
    //                         code: 'dun kere, oke?',
    //                     },
    //                 ],
    //             },
    //         }),
    //         new GraphQLError('error tu', {
    //             extensions: {
    //                 category: 'validation',
    //                 violations: [
    //                     {
    //                         path: ['user', 'firstName'],
    //                         message: 'Must be in latin',
    //                         code: 'sumthin? dunno, lel',
    //                     },
    //                 ],
    //             },
    //         }),
    //         new GraphQLError('error two', {
    //             path: ['one', 'two', 'tre'],
    //             extensions: {
    //                 category: 'coercion_error',
    //                 violation: {
    //                     path: ['user', 'email', 'first'],
    //                     message: 'Failed to parse mail input second try',
    //                 },
    //             },
    //         }),
    //         new GraphQLError('error tre', {
    //             extensions: {
    //                 category: 'coercion_error',
    //                 violation: {
    //                     path: ['user', 'email', 'second'],
    //                     message: 'Failed to parse mail input third try',
    //                 },
    //             },
    //         }),
    //         new GraphQLError('ai no london', {
    //             path: ['mi', 'veri', 'ingles'],
    //         }),
    //         new GraphQLError('Internal server error', {
    //             path: ['some', 'whatever', 'path'],
    //             extensions: {
    //                 sentryId: 'whatever I guess',
    //             },
    //         }),
    //     ]
    //
    //     test('Test useErrorCollection paths', () => {
    //         const errors = useErrorCollection(
    //             new CombinedError({
    //                 graphQLErrors: graphQLErrors,
    //             })
    //         ).value
    //
    //         expect(errors.isInternalServerError).toBe(true)
    //
    //         // Ensure path contains the GraphQL error path
    //         expect(errors.last('MySomewhereQuery.user.username')).toBe(
    //             'must be at least 2 chars'
    //         )
    //         expect(errors.has('MySomewhereQuery.user.username')).toBe(true)
    //
    //         // Ensure all violations are taken from the extensions of a validation error
    //         expect(errors.last('MySomewhereQuery.user.email')).toBe(
    //             'must be a valid email'
    //         )
    //         expect(errors.has('MySomewhereQuery.user.email')).toBe(true)
    //
    //         // Ensure empty path in a GraphQL error works & yields proper paths
    //         expect(errors.last('user.firstName')).toBe('Must be in latin')
    //         expect(errors.has('user.firstName')).toBe(true)
    //
    //         // Ensure coercion errors work
    //         expect(errors.last('one.two.tre.user.email.first')).toBe(
    //             'Failed to parse mail input second try'
    //         )
    //         expect(errors.has('one.two.tre.user.email.first')).toBe(true)
    //
    //         // Ensure coercion errors work with undefined path
    //         expect(errors.last('user.email.second')).toBe(
    //             'Failed to parse mail input third try'
    //         )
    //         expect(errors.has('user.email.second')).toBe(true)
    //
    //         // Ensure coercion errors work with undefined path
    //         expect(errors.last('mi.veri.ingles')).toBe('ai no london')
    //         expect(errors.has('mi.veri.ingles')).toBe(true)
    //     })
    //
    //     test('Test useErrorCollection with ref combined error', () => {
    //         const firstGraphQLError = new GraphQLError('error wan', {
    //             path: ['MySomewhereQuery'],
    //             extensions: {
    //                 category: 'validation',
    //                 violations: [
    //                     {
    //                         path: ['user', 'username'],
    //                         message: 'must be at least 2 chars',
    //                         code: 'sumthin? idk',
    //                     },
    //                     {
    //                         path: ['user', 'email'],
    //                         message: 'must be a valid email',
    //                         code: 'dun kere, oke?',
    //                     },
    //                 ],
    //             },
    //         })
    //
    //         const secondGraphQLError = new GraphQLError('error too', {
    //             path: ['MySomewhereQuery'],
    //             extensions: {
    //                 category: 'validation',
    //                 violations: [
    //                     {
    //                         path: ['user', 'username'],
    //                         message: 'must be at least 2 chars TU',
    //                         code: 'sumthin? idk',
    //                     },
    //                     {
    //                         path: ['user', 'email'],
    //                         message: 'must be a valid email TU WE',
    //                         code: 'dun kere, oke?',
    //                     },
    //                 ],
    //             },
    //         })
    //
    //         const combinedErrorRef = ref(
    //             new CombinedError({
    //                 graphQLErrors: [firstGraphQLError],
    //             })
    //         )
    //
    //         const errors = useErrorCollection(combinedErrorRef)
    //
    //         expect(errors.value.last('MySomewhereQuery.user.username')).toBe(
    //             'must be at least 2 chars'
    //         )
    //         expect(errors.value.last('MySomewhereQuery.user.email')).toBe(
    //             'must be a valid email'
    //         )
    //
    //         combinedErrorRef.value = new CombinedError({
    //             graphQLErrors: [secondGraphQLError],
    //         })
    //
    //         expect(errors.value.last('MySomewhereQuery.user.username')).toBe(
    //             'must be at least 2 chars TU'
    //         )
    //         expect(errors.value.last('MySomewhereQuery.user.email')).toBe(
    //             'must be a valid email TU WE'
    //         )
    //     })
    // })

    // TODO Reinstate those tests - API changed
    // describe('useMergedCollection', () => {
    //     const firstGraphQLError = new GraphQLError('error wan', {
    //         path: ['MySomewhereQuery'],
    //         extensions: {
    //             category: 'validation',
    //             violations: [
    //                 {
    //                     path: ['user', 'username'],
    //                     message: 'must be at least 2 chars',
    //                     code: 'sumthin? idk',
    //                 },
    //                 {
    //                     path: ['user', 'email'],
    //                     message: 'must be a valid email',
    //                     code: 'dun kere, oke?',
    //                 },
    //             ],
    //         },
    //     })
    //
    //     const secondGraphQLError = new GraphQLError('error tu', {
    //         extensions: {
    //             category: 'validation',
    //             violations: [
    //                 {
    //                     path: ['user', 'firstName'],
    //                     message: 'Must be in latin',
    //                     code: 'sumthin? dunno, lel',
    //                 },
    //             ],
    //         },
    //     })
    //
    //     const thirdGraphQLError = new GraphQLError('error tre', {
    //         extensions: {
    //             category: 'validation',
    //             violations: [
    //                 {
    //                     path: ['user', 'firstName'],
    //                     message: 'Must be in latin 1234 new value',
    //                     code: 'sumthin? dunno, lel 234',
    //                 },
    //             ],
    //         },
    //     })
    //
    //     const firstCombinedError = new CombinedError({
    //         graphQLErrors: [firstGraphQLError],
    //     })
    //
    //     const secondCombinedError = new CombinedError({
    //         graphQLErrors: [secondGraphQLError],
    //     })
    //
    //     const thirdCombinedError = new CombinedError({
    //         graphQLErrors: [thirdGraphQLError],
    //     })
    //
    //     test('With computed/ref array but non-ref items', () => {
    //         const firstRef = ref(firstCombinedError)
    //         const secondRef = ref(secondCombinedError)
    //
    //         const firstCollection = useErrorCollection(firstRef)
    //         const secondCollection = useErrorCollection(secondRef)
    //
    //         const errors = useMergedCollection(
    //             computed(() => [firstCollection.value, secondCollection.value])
    //         )
    //
    //         expect(errors.value.last('MySomewhereQuery.user.username')).toBe(
    //             'must be at least 2 chars'
    //         )
    //         expect(errors.value.last('MySomewhereQuery.user.email')).toBe(
    //             'must be a valid email'
    //         )
    //         expect(errors.value.last('user.firstName')).toBe('Must be in latin')
    //
    //         secondRef.value = thirdCombinedError
    //
    //         expect(errors.value.last('MySomewhereQuery.user.username')).toBe(
    //             'must be at least 2 chars'
    //         )
    //         expect(errors.value.last('MySomewhereQuery.user.email')).toBe(
    //             'must be a valid email'
    //         )
    //         expect(errors.value.last('user.firstName')).toBe(
    //             'Must be in latin 1234 new value'
    //         )
    //     })
    //
    //     test('With fixed array but computed/ref array items', () => {
    //         const firstRef = ref(firstCombinedError)
    //         const secondRef = ref(secondCombinedError)
    //
    //         const firstCollection = useErrorCollection(firstRef)
    //         const secondCollection = useErrorCollection(secondRef)
    //
    //         const errors = useMergedCollection([
    //             firstCollection,
    //             secondCollection,
    //         ])
    //
    //         expect(errors.value.last('MySomewhereQuery.user.username')).toBe(
    //             'must be at least 2 chars'
    //         )
    //         expect(errors.value.last('MySomewhereQuery.user.email')).toBe(
    //             'must be a valid email'
    //         )
    //         expect(errors.value.last('user.firstName')).toBe('Must be in latin')
    //
    //         secondRef.value = thirdCombinedError
    //
    //         expect(errors.value.last('MySomewhereQuery.user.username')).toBe(
    //             'must be at least 2 chars'
    //         )
    //         expect(errors.value.last('MySomewhereQuery.user.email')).toBe(
    //             'must be a valid email'
    //         )
    //         expect(errors.value.last('user.firstName')).toBe(
    //             'Must be in latin 1234 new value'
    //         )
    //     })
    //
    //     test('With computed/ref everything and children', () => {
    //         const firstRef = ref(firstCombinedError)
    //         const secondRef = ref(secondCombinedError)
    //
    //         const firstCollection = useErrorCollection(firstRef)
    //         const secondCollection = useErrorCollection(secondRef)
    //
    //         const errors = useMergedCollection(
    //             computed(() => [
    //                 computed(() =>
    //                     firstCollection.value.children('MySomewhereQuery')
    //                 ),
    //                 computed(() => secondCollection.value.children('user')),
    //             ])
    //         )
    //
    //         expect(errors.value.last('user.username')).toBe(
    //             'must be at least 2 chars'
    //         )
    //         expect(errors.value.last('user.email')).toBe(
    //             'must be a valid email'
    //         )
    //         expect(errors.value.last('firstName')).toBe('Must be in latin')
    //
    //         secondRef.value = thirdCombinedError
    //
    //         expect(errors.value.last('user.username')).toBe(
    //             'must be at least 2 chars'
    //         )
    //         expect(errors.value.last('user.email')).toBe(
    //             'must be a valid email'
    //         )
    //         expect(errors.value.last('firstName')).toBe(
    //             'Must be in latin 1234 new value'
    //         )
    //     })
    // })

    describe('useSimpleErrorCollection', () => {
        const firstError: BackendError = {
            message: 'not a valid email',
            path: ['user', 'email'],
            isInternalServerError: false,
            isUnauthorized: false,
        }
        const secondError: BackendError = {
            message: 'must be at least 123 chars long',
            path: ['user', 'username'],
            isInternalServerError: false,
            isUnauthorized: false,
        }
        const thirdError: BackendError = {
            message: 'using a fake box',
            path: ['user', 'email'],
            isInternalServerError: false,
            isUnauthorized: false,
        }
        const fourthError: BackendError = {
            message: 'this is another fourth username error',
            path: ['user', 'username'],
            isInternalServerError: false,
            isUnauthorized: false,
        }

        test('With computed/ref everything and children', () => {
            const thirdRef: Ref<BackendError | null | undefined> =
                ref(thirdError)

            // eslint-disable-next-line vue-composable/composable-placement
            const errors = useSimpleErrorCollection(
                computed(() => [
                    computed(() => firstError),
                    secondError,
                    thirdRef,
                    undefined,
                    ref(undefined),
                ])
            )

            expect(errors.value.last('user.username')).toBe(
                'must be at least 123 chars long'
            )
            expect(errors.value.last('user.email')).toBe('using a fake box')

            thirdRef.value = fourthError

            expect(errors.value.last('user.username')).toBe(
                'this is another fourth username error'
            )
            expect(errors.value.last('user.email')).toBe('not a valid email')

            thirdRef.value = null

            expect(errors.value.last('user.username')).toBe(
                'must be at least 123 chars long'
            )
            expect(errors.value.last('user.email')).toBe('not a valid email')
        })
    })

    describe('useLastError', () => {
        const firstGraphQLError = new GraphQLError('error wan', {
            path: ['MySomewhereQuery1'],
            extensions: {
                category: 'validation',
                violations: [
                    {
                        path: ['user', 'username'],
                        message: 'must be at least 2 chars',
                        code: 'sumthin? idk',
                    },
                    {
                        path: ['user', 'email'],
                        message: 'must be a valid email',
                        code: 'dun kere, oke?',
                    },
                ],
            },
        })

        const secondGraphQLError = new GraphQLError('error too', {
            path: ['MySomewhereQuery2'],
            extensions: {
                category: 'validation',
                violations: [
                    {
                        path: ['user', 'username'],
                        message: 'must be at least 2 chars TU',
                        code: 'sumthin? idk',
                    },
                    {
                        path: ['user', 'email'],
                        message: 'must be a valid email TU WE',
                        code: 'dun kere, oke?',
                    },
                ],
            },
        })

        const thirdGraphQLError = new GraphQLError('error tre', {
            path: ['MySomewhereQuery1'],
            extensions: {
                category: 'validation',
                violations: [
                    {
                        path: ['user', 'username'],
                        message: 'must be at least 6 chars',
                        code: 'sumthin? idk',
                    },
                    {
                        path: ['user', 'email'],
                        message: 'must be a valid email with something more',
                        code: 'dun kere, oke?',
                    },
                ],
            },
        })

        const firstCombinedError = new CombinedError({
            graphQLErrors: [firstGraphQLError],
        })
        const secondCombinedError = new CombinedError({
            graphQLErrors: [secondGraphQLError],
        })
        const thirdCombinedError = new CombinedError({
            graphQLErrors: [thirdGraphQLError],
        })

        test('useLastError', async () => {
            type MyType1 = { MySomewhereQuery1: string }
            const data1: MyType1 = {
                MySomewhereQuery1: 'test',
            }

            const response1 = {
                data: ref<MyType1 | undefined>(data1),
                error: ref<CombinedError>(firstCombinedError),
                extensions: ref({}),
            }

            // const firstRef = ref(
            //     createResponseErrorWithPath(response1, 'MySomewhereQuery1')
            // )
            const firstRef = createResponseAndPath(
                response1,
                'MySomewhereQuery1'
            )

            type MyType2 = { MySomewhereQuery2: string }
            const data2: MyType2 = {
                MySomewhereQuery2: 'test',
            }

            const response2 = {
                data: ref<MyType2 | undefined>(data2),
                error: ref<CombinedError | undefined>(undefined),
                extensions: ref({}),
            }
            // todo change

            const secondRef = createResponseAndPath(
                response2,
                'MySomewhereQuery2'
            )

            // eslint-disable-next-line vue-composable/composable-placement
            const errors = useLastResponseErrorAtPath([firstRef, secondRef])

            // First time around, response2's error is undefined, so we get response1 errors
            expect(errors.value.last('user.username')).toBe(
                'must be at least 2 chars'
            )
            expect(errors.value.last('user.email')).toBe(
                'must be a valid email'
            )

            // Then we change response2's error to something
            response2.error.value = secondCombinedError

            await nextTick()

            expect(errors.value.last('user.username')).toBe(
                'must be at least 2 chars TU'
            )
            expect(errors.value.last('user.email')).toBe(
                'must be a valid email TU WE'
            )

            // And last we test if we change response1 error that it swaps back to showing the last error that changed
            response1.error.value = thirdCombinedError

            await nextTick()

            expect(errors.value.last('user.username')).toBe(
                'must be at least 6 chars'
            )
            expect(errors.value.last('user.email')).toBe(
                'must be a valid email with something more'
            )
        })
    })

    describe('useCombinedErrorFromResponse', () => {
        test('useCombinedErrorFromResponse', () => {
            type Data = {
                myRootQuerySomething: {
                    me: 'test'
                }
            }
            const dataRef = ref(undefined)
            const errorRef: Ref<CombinedError | undefined> = ref(undefined)
            const response: MutationOrQueryState<Data> = {
                data: dataRef,
                error: errorRef,
                extensions: ref({}),
            }

            const combined = createResponseAndPath(
                response,
                'myRootQuerySomething'
            )

            // eslint-disable-next-line vue-composable/composable-placement
            const errors = useLastResponseErrorAtPath([combined])

            expect(combined.path).toBe('myRootQuerySomething')
            expect(combined.response.error.value).toBeUndefined()

            expect(errors.value.last('user.username')).toBeUndefined()
            expect(errors.value.last('user.email')).toBeUndefined()

            const thirdGraphQLError = new GraphQLError('error tre', {
                path: ['myRootQuerySomething'],
                extensions: {
                    category: 'validation',
                    violations: [
                        {
                            path: ['user', 'username'],
                            message: 'must be at least 6 chars',
                            code: 'sumthin? idk',
                        },
                        {
                            path: ['user', 'email'],
                            message:
                                'must be a valid email with something more',
                            code: 'dun kere, oke?',
                        },
                    ],
                },
            })

            errorRef.value = new CombinedError({
                graphQLErrors: [thirdGraphQLError],
            })

            nextTick(() => {
                expect(combined.path).toBe('myRootQuerySomething')
                expect(combined.response.error.value).not.toBeUndefined()

                expect(errors.value.last('user.username')).toBe(
                    'must be at least 6 chars'
                )
                expect(errors.value.last('user.email')).toBe(
                    'must be a valid email with something more'
                )
            })
        })
    })
}
